import React, { useState, useCallback, useMemo } from "react";
import { Box, Modal, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyntaxHighlightedCode from "./SyntaxHighlightedCode";

const QuestionContent = React.memo(
  ({
    question,
    className = "sun-editor-editable",
    style = {},
    onClick,
    ...props
  }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [openImageModal, setOpenImageModal] = useState(false);

    // Handle image click to open preview modal
    const handleImageClick = useCallback((event) => {
      // Check if the clicked element is an image or inside an image
      const target = event.target;
      if (target.tagName === "IMG") {
        event.preventDefault();
        event.stopPropagation();
        setImageUrl(target.src);
        setOpenImageModal(true);
      } else {
        // Check if clicked element is inside an image container
        const imgElement = target.closest("img");
        if (imgElement) {
          event.preventDefault();
          event.stopPropagation();
          setImageUrl(imgElement.src);
          setOpenImageModal(true);
        }
      }
    }, []);

    // Extract image title from alt attribute or use default
    const getImageTitle = useCallback(
      (src) => {
        if (!src) return "Question Image";

        // Try to find the original image element to get its alt text
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = question;
        const imgElement = tempDiv.querySelector(`img[src="${src}"]`);

        if (imgElement && imgElement.alt) {
          return imgElement.alt;
        }

        // Fallback to filename or default title
        const filename = src.split("/").pop();
        return filename || "Question Image";
      },
      [question]
    );

    // Handle close image modal
    const handleCloseImageModal = useCallback(() => {
      setOpenImageModal(false);
      setImageUrl(null);
    }, []);

    // Check if question contains images to show pointer cursor
    const hasImages = useMemo(() => {
      return question && question.includes("<img");
    }, [question]);

    // Combined click handler
    const handleCombinedClick = useCallback(
      (event) => {
        // First handle image click
        handleImageClick(event);

        // Then call the original onClick if provided
        if (onClick && !event.defaultPrevented) {
          onClick(event);
        }
      },
      [handleImageClick, onClick]
    );

    // Modal style for image preview
    const modalStyle = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "background.paper",
      boxShadow: 24,
      borderRadius: "12px",
      maxWidth: "95%",
      maxHeight: "95%",
      width: "auto",
      minWidth: "600px",
      minHeight: "500px",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      outline: "none",
    };

    // Default styles for question content
    const defaultStyle = {
      padding: "10px",
      fontFamily: "Roboto, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      width: "100%",
      cursor: hasImages ? "pointer" : "default",
      ...style,
    };

    // Render content with code blocks using reusable component
    const renderContent = () => {
      // SyntaxHighlightedCode component always returns content:
      // - Original HTML if no code blocks found
      // - Mixed content (HTML + code blocks) if code blocks exist
      return (
        <div
          className={className}
          style={defaultStyle}
          onClick={handleCombinedClick}
          {...props}
        >
          <SyntaxHighlightedCode
            content={question}
            onCodeBlockParsed={(result) => {
              // Optional: Debug logging
              if (result.hasCodeBlocks) {
                console.log("🔍 Code Block Detection:", {
                  questionPreview: question.substring(0, 300),
                  hasCodeBlocks: result.hasCodeBlocks,
                  partsCount: result.parts.length,
                });
              }
            }}
          />
        </div>
      );
    };

    return (
      <>
        {renderContent()}

        {/* Image Preview Modal */}
        <Modal
          open={openImageModal}
          onClose={handleCloseImageModal}
          aria-labelledby="image-preview-modal"
          aria-describedby="image-preview-description"
        >
          <Box sx={modalStyle}>
            {/* Header with title and close button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "12px 12px 0 0",
                minHeight: "60px",
                flexShrink: 0,
              }}
            >
              <Typography
                id="image-preview-modal"
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "text.primary",
                  flex: 1,
                  mr: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {imageUrl ? getImageTitle(imageUrl) : "Question Image"}
              </Typography>

              <IconButton
                onClick={handleCloseImageModal}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    color: "text.primary",
                    bgcolor: "action.hover",
                  },
                  borderRadius: "50%",
                  p: 1,
                  flexShrink: 0,
                }}
                aria-label="Close image preview"
              >
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Box>

            {/* Image content */}
            <Box
              id="image-preview-description"
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                bgcolor: "#f8f9fa",
                minHeight: "400px",
                maxHeight: "calc(95vh - 100px)",
                overflow: "auto",
                position: "relative",
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={getImageTitle(imageUrl)}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    minWidth: "500px",
                    minHeight: "400px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    backgroundColor: "white",
                    padding: "12px",
                  }}
                />
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  No image available
                </Typography>
              )}
            </Box>
          </Box>
        </Modal>
      </>
    );
  }
);

QuestionContent.displayName = "QuestionContent";

export default QuestionContent;
